// useCapabilityReporter — o runner (Mac, app aberto) anuncia periodicamente
// quais cérebros estão disponíveis (modelos cloud configurados + Ollama/LM
// Studio online) via POST /api/dispatch/runner/capabilities. O celular lê isso
// (BrainStatus) antes de disparar. Best-effort; só roda no Electron.

import { useEffect, useRef } from 'react';
import { postCapabilities, postHeartbeat } from '../lib/remoteDispatchClient.js';
import { isLocalAdapter } from './useUnifiedModels.js';

const REPORT_INTERVAL_MS = 30_000;
// The presence heartbeat MUST fire faster than the server's presence window
// (RUNNER_ONLINE_MS = 5s in remoteDispatch.js / enqueueAndRouteRun.js). It was
// piggy-backed on the 30s capability report, so the Mac read "online" only ~5s
// out of every 30s and the scheduler routed Hermes/Radar runs to the toolless
// cloud loop instead of the local with-tools runner. Heartbeat on its own fast
// cadence; capabilities (which probe Ollama/LM Studio) stay at 30s.
const HEARTBEAT_INTERVAL_MS = 4_000;

/** Monta o snapshot de capabilities a partir dos adapters + status do Ollama. */
export async function gatherCapabilities(adapters) {
  const cloudModels = (adapters || [])
    .filter((a) => !isLocalAdapter(a) && a.is_active !== false && a.is_active !== 0)
    .flatMap((a) => a.available_models || []);

  let ollama = { online: false, models: [] };
  try {
    const r = await window.apolo?.ollama?.listModels?.();
    if (r && (r.ok || Array.isArray(r.models))) {
      ollama = { online: true, models: r.models || [] };
    }
  } catch {
    /* offline — fica { online:false } */
  }

  let lmstudio = { online: false, models: [] };
  try {
    const status = await window.apolo?.lmstudio?.status?.();
    if (status?.ok || status?.online) {
      const ls = await window.apolo?.lmstudio?.ls?.();
      const models = Array.isArray(ls?.models) ? ls.models : (Array.isArray(ls?.data) ? ls.data : []);
      lmstudio = { online: true, models: models.map((m) => (typeof m === 'string' ? m : m?.id || m?.name)).filter(Boolean) };
    }
  } catch {
    /* offline */
  }

  // E3-WS3 honesty: can THIS runner drive a browser? The chromeMcp bridge is
  // only present in the Electron runner (preload exposes window.apolo.browser).
  // A cheap presence check (no spawn) — the runner endpoint surfaces this as
  // `browserAvailable` so the UI is honest and the continuous driver knows a
  // browser-needing chunk CAN run here (if Chrome is up; web_act reports the
  // honest error otherwise, and the run shows acted=0 rather than fake-done).
  let browserAvailable = false;
  try { browserAvailable = (await window.apolo?.browser?.chromeMcp?.status?.())?.connected === true; }
  catch { /* An installed bridge is not evidence of a connected browser. */ }

  return { cloudModels, ollama, lmstudio, browserAvailable };
}

export function useCapabilityReporter({
  enabled = false,
  adapters,
  intervalMs = REPORT_INTERVAL_MS,
  heartbeatMs = HEARTBEAT_INTERVAL_MS,
} = {}) {
  const adaptersRef = useRef(adapters);
  useEffect(() => {
    adaptersRef.current = adapters;
  }, [adapters]);

  // Capabilities: probe the local brains (Ollama/LM Studio) + cloud adapters on
  // the slow cadence — this is what the phone's BrainStatus reads.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    async function report() {
      if (cancelled) return;
      try {
        await postCapabilities(await gatherCapabilities(adaptersRef.current));
      } catch {
        /* best-effort */
      }
    }
    report();
    const t = setInterval(report, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [enabled, intervalMs]);

  // Presence heartbeat: its OWN fast cadence (< the 5s server presence window)
  // so "Mac online" stays solid and the scheduler routes runs to the local
  // with-tools runner. Cheap (no local probing) — just a liveness ping.
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    async function beat() {
      if (cancelled) return;
      try {
        await postHeartbeat();
      } catch {
        /* best-effort */
      }
    }
    beat();
    const t = setInterval(beat, heartbeatMs);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [enabled, heartbeatMs]);

  return null;
}
